import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { X, Share2, Video } from "lucide-react";

import Images from "./Images";
import Detail from "./Detail";
import Tour from "./Tour";
import ShareModal from "./ShareModal";
import XHSVideoModal from "../Appointments/MyListings/XHSVideoModal";

import * as propertyImgActions from "../../store/property_img";
import * as agentActions from "../../store/agent";
import { getWhitelabelSlug } from "../../utils/whitelabel";
import apiFetch from "../../utils/apiFetch";

// Injects listing JSON for the Tourit→FBMP Chrome extension.
// Runs on every listing open (modal AND standalone page).
function useFbmpEmbed(property) {
	useEffect(() => {
		if (!property) return;
		const street = property.street || "";
		const unit = (property.unit || property.unit_number) ? `#${property.unit || property.unit_number} ` : "";
		const address = `${unit}${street}`.trim();
		const payload = {
			mls_number: property.mls_number,
			title: `${property.bed ?? "?"}BR ${property.style || property.property_type || "Home"} for Rent | ${address}, ${property.city || ""}`,
			price: property.price,
			description: property.description || "",
			address,
			city: property.city || "",
			state: property.state || "",
			zip: property.zip || "",
			beds: property.bed,
			baths: property.bath,
			property_type: property.property_type || "",
			style: property.style || "",
			images: property.images || [],
		};
		let el = document.getElementById("tourit-listing-data");
		if (!el) {
			el = document.createElement("script");
			el.id = "tourit-listing-data";
			el.type = "application/json";
			document.head.appendChild(el);
		}
		el.textContent = JSON.stringify(payload);
		return () => { try { el.remove(); } catch {} };
	}, [property]);
}

const Property = ({ property, onClose, referralAgent = null, isPage = false }) => {
	const dispatch = useDispatch();
	const user = useSelector((state) => state.session.user);
	const listingAgent = useSelector((state) =>
		property?.listing_agent_id ? state.agent[property.listing_agent_id] : null
	);
	const [showMobileTour, setShowMobileTour] = useState(false);
	const [showShare, setShowShare] = useState(false);
	const [showXhsModal, setShowXhsModal] = useState(false);
	const [listingVideoUrl, setListingVideoUrl] = useState(null);

	useFbmpEmbed(property);

	const buildShareUrl = () => {
		const mlsNum = property?.mls_number || property?.listing_id;
		const origin = window.location.origin;
		const agentId = referralAgent?.id || (user?.agent && !referralAgent ? user.id : null);
		const wlSlug = getWhitelabelSlug();
		const base = `${origin}/share/listing/${encodeURIComponent(mlsNum)}`;
		const params = new URLSearchParams();
		if (agentId) params.set("agent", String(agentId));
		// If on a whitelabel and no agent id, still hint Flask so canonical stays on this domain
		if (wlSlug && !agentId) params.set("wl", wlSlug);
		const qs = params.toString();
		return qs ? `${base}?${qs}` : base;
	};

	useEffect(() => {
		if (!property?.is_mls && property?.id != null) {
			dispatch(propertyImgActions.getAllImages(property.id));
		}
		if (property.listing_agent_id != null) {
			dispatch(agentActions.getThisAgent(property.listing_agent_id));
		}
	}, [property, dispatch]);

	useEffect(() => {
		const mls = property?.mls_number;
		if (!mls) return;
		setListingVideoUrl(null);
		apiFetch(`/api/xhs/listing-video/${encodeURIComponent(mls)}`)
			.then(r => r.json())
			.then(d => { if (d.video_url) setListingVideoUrl(d.video_url); })
			.catch(() => {});
	}, [property?.mls_number]); // eslint-disable-line react-hooks/exhaustive-deps

	return (
		<div className="relative bg-white w-[96vw] max-w-[1350px] max-h-[92vh] rounded-2xl flex flex-col">

			{/* Close + Share + XHS Video buttons */}
			<div className="absolute top-3 right-3 z-30 flex items-center gap-1.5">
				{user?.agent && property?.is_mls && (
					<button
						type="button"
						className="flex items-center gap-1 px-2 h-8 rounded-full bg-white/90 shadow text-gray-500 hover:text-red-500 transition-colors text-xs font-medium"
						onClick={() => setShowXhsModal(true)}
						title="生成小红书看房视频"
					>
						<Video size={13} strokeWidth={2} />
						<span className="hidden sm:inline">小红书视频</span>
					</button>
				)}
				<button
					type="button"
					className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 shadow text-gray-500 hover:text-gray-900 transition-colors"
					onClick={() => setShowShare(true)}
					title="Share listing"
				>
					<Share2 size={14} strokeWidth={2} />
				</button>
				<button
					type="button"
					className="flex items-center justify-center w-8 h-8 rounded-full bg-white/90 shadow text-gray-500 hover:text-gray-900 transition-colors"
					onClick={(e) => { e.stopPropagation(); onClose(); }}
				>
					<X size={16} strokeWidth={2} />
				</button>
			</div>

			{/* Single scroll container — scrollbar on far right */}
			<div className={`overflow-y-auto flex-1 min-h-0 rounded-2xl${isPage ? " pb-20 md:pb-0" : ""}`}>
				<div className="flex items-start">

					{/* Left: gallery + detail */}
					<div className="flex-1 min-w-0">
						<Images property={property} />
						{listingVideoUrl && (
							<div className="px-6 pt-5 pb-2">
								<p className="text-sm font-semibold text-[#1a1a1a] mb-2 notranslate">房源视频</p>
								<video
									src={listingVideoUrl}
									controls
									playsInline
									className="w-full rounded-xl bg-black"
									style={{ maxHeight: 500 }}
								/>
							</div>
						)}
						<div className="px-6 py-6">
							<Detail property={property} />
						</div>
					</div>

										{/* Right: Tour — sticky so it stays at top while left column scrolls */}
										<div className="max-sm:hidden flex-shrink-0 w-[350px] sticky top-0 self-start border-l border-gray-100 p-5">
											<Tour property={property} setShowTour={onClose} inline referralAgent={referralAgent} />
										</div>

				</div>
			</div>

			{/* Mobile: floating Tour button */}
			{!showMobileTour && (
				<div className={`md:hidden ${isPage ? "fixed" : "absolute"} bottom-0 left-0 right-0 px-4 pb-4 pt-10 bg-gradient-to-t from-white via-white/90 to-transparent pointer-events-none z-30`}>
					<button
						type="button"
						className="pointer-events-auto w-full rounded-xl bg-[#0f172a] py-3.5 text-sm font-semibold text-white shadow-lg active:opacity-80 transition-opacity"
						onClick={() => setShowMobileTour(true)}
					>
						{referralAgent ? `Tour with ${referralAgent.username}` : "Tour with a Buyer's Agent"}
					</button>
				</div>
			)}

			{/* XHS video generation modal — agents only */}
			{showXhsModal && (
				<XHSVideoModal
					listing={property}
					onClose={() => setShowXhsModal(false)}
				/>
			)}

			{/* Share modal — card preview + save image + copy link */}
			{showShare && (
				<ShareModal
					property={property}
					shareUrl={buildShareUrl()}
					agent={referralAgent || (user?.agent ? user : null) || listingAgent || null}
					onClose={() => setShowShare(false)}
				/>
			)}

			{/* Mobile: slide-up Tour panel */}
			{showMobileTour && (
				<div className={`md:hidden ${isPage ? "fixed" : "absolute"} inset-x-0 bottom-0 bg-white rounded-t-2xl shadow-2xl z-40 max-h-[88%] flex flex-col`}>
					<div className="flex items-center justify-center relative pt-3 pb-1 flex-shrink-0">
						<div className="w-10 h-1 rounded-full bg-[#e2e8f0]" />
						<button
							type="button"
							className="absolute right-3 top-2 flex items-center justify-center w-8 h-8 rounded-full bg-[#f1f1ee] text-gray-500"
							onClick={() => setShowMobileTour(false)}
						>
							<X size={14} />
						</button>
					</div>
					<div className="overflow-y-auto flex-1 min-h-0">
						<Tour
							property={property}
							setShowTour={() => setShowMobileTour(false)}
							inline
							referralAgent={referralAgent}
						/>
					</div>
				</div>
			)}
		</div>
	);
};

export default Property;
